import React, { useEffect, useId, useMemo, useState } from 'react';

import { ModalTitleContextProvider } from '@/ui/Modal/ModalTitleContext';
import { Backdrop, Close, MainBox, OpenCloseTransition, RootContainer } from './atoms';
import { Progress } from './atoms/Progress';
import { ModalProps } from './types';

export const Modal = ({
  open,
  children,
  onClose,
  progress,
  closeOnEscapeKey = true,
  size,
  hasTransition = true,
  className,
  ariaLabel,
}: ModalProps): JSX.Element => {
  const titleId = useId();
  const [hasTitle, setHasTitle] = useState(false);
  const titleContext = useMemo(() => ({ titleId, setHasTitle }), [titleId]);

  useEffect(() => {
    if (closeOnEscapeKey && onClose) {
      const close = ({ key }: KeyboardEvent) => key === 'Escape' && onClose();
      document.addEventListener('keyup', close);
      return () => document.removeEventListener('keyup', close);
    }
  }, [onClose, closeOnEscapeKey]);

  const mainBox = (
    // `role` is spelled out rather than left to the `<dialog>` element's own mapping: the element
    // is shown through CSS, never opened with `showModal()`, so its implicit semantics are not
    // something assistive technology can be relied on to report. `aria-modal` is what declares the
    // background inert — mui-base already aria-hides the siblings and traps focus.
    <MainBox
      size={size}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby={hasTitle ? titleId : undefined}
      aria-label={hasTitle ? undefined : ariaLabel}
    >
      {progress && (
        <Progress animationStartPercentage={progress.from} value={progress.to} roundedBar={false} roundedTrack={false} animated />
      )}
      {onClose && <Close onClick={onClose} />}
      {children}
    </MainBox>
  );

  return (
    <ModalTitleContextProvider value={titleContext}>
      <RootContainer
        open={open}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        // We need a loose type here due to MUI's unprecise type defs.
        // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-explicit-any
        slotProps={{
          backdrop: { open } as any,
          root: { 'data-testid': 'modalRoot' } as any,
        }}
        className={className}
      >
        {hasTransition ? <OpenCloseTransition in={open}>{mainBox}</OpenCloseTransition> : mainBox}
      </RootContainer>
    </ModalTitleContextProvider>
  );
};
